import React from 'react';
import PropTypes from 'prop-types';

// Module
import ErrorHandler from '../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import FavoriteButtonCompany from './FavoriteButtonCompany';
import CompanyProfileCard from './CompanyProfileCard';
import CompanyProfileDetailsCard from './CompanyProfileDetailsCard';
import CompanyProfileFeaturedSection from './CompanyProfileFeaturedSection';
import CompanyProfileUpcomingEvents from './CompanyProfileUpcomingEvents';
import CompanyProfileAffiliatedMembers from './CompanyProfileAffiliatedMembers';
import CompanyProfileCompaniesSimilarTo from './CompanyProfileCompaniesSimilarTo';
import CompanyProfilePropertyListings from './CompanyProfilePropertyListings';

// Shared
import { H3, SectionWrapperInner, SectionWrapperOuter } from '@styled';
import LoadingArea from '../../../../../_shared-react/components/_molecules/LoadingArea';

// Functionality
import useCompanyFull from '../../../../../_shared-react/hooks/useCompanyFull';
import useIsCompanyFavorite from '@hooks/useIsCompanyFavorite';
import { getCompanyDisplayType } from '@general/helpers/company.helpers';
import useDocumentTitle from '@hooks/useDocumentTitle';

const CompanyProfile = ({ graphID }) => {
    const { data: companyData, loading } = useCompanyFull(graphID);
    const isFavorite = useIsCompanyFavorite(graphID);
    const { documentTitle } = useDocumentTitle(companyData?.name);

    if (loading) {
        return <LoadingArea />;
    }

    if (!companyData) {
        return <LoadingArea />;
    }

    const { primaryBusinessType } = companyData;
    const companyDisplayType = getCompanyDisplayType(primaryBusinessType);

    return (
        <ErrorHandler>
            <TrackPageView>
                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <div className="fade-in-fast pb-4">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <H3 className="mt-2 mb-3">{documentTitle}</H3>
                                <div style={{ display: 'flex' }}>
                                    {typeof isFavorite !== 'undefined' && (
                                        <span style={{ marginLeft: '18px' }}>
                                            <FavoriteButtonCompany
                                                recordID={companyData.recordId}
                                                isFavorite={isFavorite}
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex-wrap row mb-4">
                                <div className="col-lg-4 d-flex mb-4 mb-lg-0">
                                    <CompanyProfileCard graphID={graphID} />
                                </div>

                                <div className="col-lg-8 d-flex flex-column">
                                    <CompanyProfileDetailsCard graphID={graphID} />
                                </div>
                            </div>

                            {(companyDisplayType === 'OwnerDeveloper' || companyDisplayType === 'Broker') && (
                                <div className="flex-wrap row mb-4">
                                    <CompanyProfilePropertyListings graphID={graphID} />
                                </div>
                            )}

                            <div className="flex-wrap row mb-4">
                                <CompanyProfileFeaturedSection graphID={graphID} />
                            </div>

                            <div className="flex-wrap row mb-4">
                                <CompanyProfileUpcomingEvents graphID={graphID} />
                            </div>

                            <div className="flex-wrap row mb-4">
                                <CompanyProfileAffiliatedMembers graphID={graphID} />
                            </div>

                            <div className="flex-wrap row mb-4">
                                <CompanyProfileCompaniesSimilarTo graphID={graphID} />
                            </div>
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

CompanyProfile.propTypes = {
    graphID: PropTypes.string,
};

export default CompanyProfile;
